<script>
export default {
  name: "MultiSelect",
  props: {
    modelValue: {type: Array, default: () => []},
    options: {type: Array, default: () => []},
    placeholder: {type: String, default: "Не выбрано"},
    disabled: {type: Boolean, default: false},
    valueKey: {type: String, default: "value"},
    labelKey: {type: String, default: "label"},
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
    };
  },
  computed: {
    selectedValues() {
      return this.modelValue || [];
    },
    selectedLabels() {
      return this.options
          .filter(option => this.selectedValues.includes(this.optionValue(option)))
          .map(option => this.optionLabel(option));
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.clickOutsideHandler);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.clickOutsideHandler);
  },
  methods: {
    optionValue(option) {
      return option ? option[this.valueKey] : undefined;
    },
    optionLabel(option) {
      return option ? option[this.labelKey] : undefined;
    },
    isSelected(value) {
      return this.selectedValues.includes(value);
    },
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
    },
    toggleOption(value) {
      const next = this.isSelected(value)
          ? this.selectedValues.filter(item => item !== value)
          : [...this.selectedValues, value];
      this.$emit('update:modelValue', next);
    },
    clickOutsideHandler(e) {
      if (this.$refs.rootRef && !this.$refs.rootRef.contains(e.target)) {
        this.open = false;
      }
    },
  },
}
</script>

<template>
  <div ref="rootRef" class="multiSelect">
    <button
        type="button"
        class="multiSelect__control"
        :class="{ 'multiSelect__control--open': open, 'multiSelect__control--disabled': disabled }"
        :disabled="disabled"
        @click="toggle"
    >
      <span v-if="selectedLabels.length" class="multiSelect__value">{{ selectedLabels.join(', ') }}</span>
      <span v-else class="multiSelect__placeholder">{{ placeholder }}</span>
      <svg class="multiSelect__arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    <div v-if="open" class="multiSelect__dropdown">
      <label
          v-for="option in options"
          :key="optionValue(option)"
          class="multiSelect__option"
      >
        <input
            type="checkbox"
            class="multiSelect__checkbox"
            :checked="isSelected(optionValue(option))"
            @change="toggleOption(optionValue(option))"
        />
        <span class="multiSelect__optionLabel">{{ optionLabel(option) }}</span>
      </label>
      <div v-if="!options.length" class="multiSelect__empty">Нет доступных ролей</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.multiSelect {
  position: relative;
  width: 100%;

  &__control {
    width: 100%;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);
    text-align: left;
    cursor: pointer;
    transition: border-color 0.3s;

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }

    &--disabled {
      cursor: default;
      opacity: 0.6;
    }
  }

  &__value {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__placeholder {
    flex: 1;
    color: var(--cd-field-color-disabled);
  }

  &__arrow {
    flex-shrink: 0;
    transition: transform 0.3s;
  }

  &__control--open &__arrow {
    transform: rotate(180deg);
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    background: var(--cd-card-bg);
    border: 1px solid var(--cd-field-border);
    border-radius: 12px;
    padding: 8px;
    max-height: 220px;
    overflow-y: auto;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: var(--cd-text);
    margin: 0;

    &:hover {
      background: var(--cd-button-bg-color);
    }
  }

  &__checkbox {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    accent-color: var(--cd-base-green);
    cursor: pointer;
  }

  &__empty {
    padding: 8px 10px;
    font-size: 13px;
    color: var(--cd-text-muted);
  }
}
</style>
